import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import { api, uploadWithProgress } from "../api";
import { FORUM_CATEGORIES as SITE_CATEGORIES } from "../../site/data";
import { LAONA_CATEGORIES } from "../../laona/data";
import Markdown from "../../site/Markdown";
import StickyHead from "../StickyHead";

/* 홈페이지 포럼(/forum) 관리 — 글은 여기서만 쓸 수 있다.
   탭: 글 · 댓글 문의  (?tab=posts|comments)
   본문은 마크다운, 사진은 본문에 바로 넣고, 내려받을 파일은 자료실 저장소에 올려 첨부로 건다. */

/* I.META 포럼 분류 + 라오나봇 자료실 분류 (라오나봇 글은 /laonabot/library 에만 나온다) */
const FORUM_CATEGORIES = [
  ...SITE_CATEGORIES,
  ...LAONA_CATEGORIES.map((c) => ({ id: c.id, label: `라오나봇 · ${c.label}` })),
];
const CAT_LABEL = Object.fromEntries(FORUM_CATEGORIES.map((c) => [c.id, c.label]));
const LAONA_IDS = new Set(LAONA_CATEGORIES.map((c) => c.id));
const siteUrl = (p) => (LAONA_IDS.has(p.category) ? `/laonabot/library/${p.id}` : `/forum/${p.id}`);
const EMPTY = { category: "", title: "", summary: "", body: "", attachments: [], is_public: false, is_pinned: false };

const fmtSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

function toForm(p) {
  return {
    category: p.category || "", title: p.title || "", summary: p.summary || "", body: p.body || "",
    attachments: p.attachments || [], is_public: Boolean(p.is_public), is_pinned: Boolean(p.is_pinned),
  };
}

/* 편집 팝업 — 바깥을 눌러도, ESC 를 눌러도 닫히지 않는다(개발 사례 관리와 같은 규칙).
   dockRef 를 주면 제목줄 바로 아래, 스크롤되지 않는 자리에 도구 막대 칸을 둔다. */
function Modal({ title, onClose, busy, dockRef, children }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div className="sstoa-modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="sstoa-modal__box sstoa-modal__box--wide">
        <div className="sstoa-modal__head">
          <h2 className="sstoa-modal__title">{title}</h2>
          <button type="button" className="sstoa-modal__x" onClick={onClose} disabled={busy} aria-label="닫기">×</button>
        </div>
        {dockRef && <div ref={dockRef} className="forum-dock" />}
        {children}
      </div>
    </div>
  );
}

/* 클립보드 · 끌어다 놓기에서 사진 파일만 골라낸다.
   캡처 도구에 따라 files 가 비고 items 에만 들어오는 경우가 있어 둘 다 본다. */
function imageFiles(data) {
  if (!data) return [];
  const files = Array.from(data.files || []);
  if (!files.length && data.items) {
    for (const it of Array.from(data.items)) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
  }
  return files.filter((f) => f.type.startsWith("image/"));
}

/* ---------------------------------------------------------------- 본문 편집기 */
/* dock 이 있으면 도구 막대를 그 자리(팝업 맨 위)에 띄워, 아래로 스크롤해도 늘 보이게 한다 */
function BodyEditor({ value, onChange, busy, dock, onAttach }) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const latest = useRef(value); // 사진을 올리는 동안 이어 쓴 글자가 사라지지 않게 최신 본문을 본다
  latest.current = value;
  const [mode, setMode] = useState("split"); // split | write | preview
  const [uploading, setUploading] = useState("");
  const [error, setError] = useState("");

  /* 선택한 글자를 앞뒤로 감싸거나, 커서 자리에 끼워 넣는다 */
  function wrap(before, after = "", placeholder = "") {
    const ta = ref.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e } = ta;
    const picked = value.slice(s, e) || placeholder;
    const next = value.slice(0, s) + before + picked + after + value.slice(e);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(s + before.length, s + before.length + picked.length);
    });
  }

  /* 줄 맨 앞에 붙는 표시(제목 · 목록 · 인용) */
  function linePrefix(prefix) {
    const ta = ref.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e } = ta;
    const start = value.lastIndexOf("\n", s - 1) + 1;
    const block = value.slice(start, e).split("\n").map((l) => prefix + l).join("\n");
    onChange(value.slice(0, start) + block + value.slice(e));
    requestAnimationFrame(() => ta.focus());
  }

  function insertBlock(text) {
    const value = latest.current;
    const ta = ref.current;
    const pos = ta ? ta.selectionEnd : value.length;
    const before = value.slice(0, pos);
    const lead = before && !before.endsWith("\n\n") ? (before.endsWith("\n") ? "\n" : "\n\n") : "";
    const next = before + lead + text + "\n\n" + value.slice(pos).replace(/^\n+/, "");
    onChange(next);
    const caret = (before + lead + text + "\n\n").length;
    requestAnimationFrame(() => { if (ta) { ta.focus(); ta.setSelectionRange(caret, caret); } });
  }

  async function uploadImages(files) {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    setError("");
    const lines = [];
    try {
      for (let i = 0; i < list.length; i += 1) {
        setUploading(`사진 올리는 중… (${i + 1}/${list.length})`);
        const fd = new FormData();
        fd.append("file", list[i]);
        const res = await api.upload("/forum/images", fd);
        lines.push(`![](${res.url})`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading("");
      if (lines.length) insertBlock(lines.join("\n\n"));
    }
  }

  /* 캡처한 사진을 Ctrl+V 하면 바로 올리고 커서 자리에 넣는다(글자 붙여넣기는 그대로) */
  function onPaste(e) {
    const files = imageFiles(e.clipboardData);
    if (files.length) {
      e.preventDefault();
      uploadImages(files);
    }
  }

  /* 사진은 본문에, 그 밖의 파일(zip 등)은 첨부 파일로 보낸다 */
  function onDrop(e) {
    const all = Array.from(e.dataTransfer?.files || []);
    if (!all.length) return;
    e.preventDefault();
    const images = all.filter((f) => f.type.startsWith("image/"));
    const others = all.filter((f) => !f.type.startsWith("image/"));
    if (images.length) uploadImages(images);
    if (others.length && onAttach) onAttach(others);
  }

  /* 미리보기에서 사진 모서리를 끌어 바꾼 폭을 그 줄의 마크다운에 적는다 — ![설명|60%](주소) */
  function resizeImage(line, pct) {
    const rows = latest.current.split("\n");
    if (rows[line] === undefined) return;
    rows[line] = rows[line].replace(/!\[([^\]]*)\]/, (_, alt) => `![${alt.replace(/\|\s*\d+(px|%)?\s*$/, "")}|${pct}%]`);
    onChange(rows.join("\n"));
  }

  const disabled = busy || Boolean(uploading);
  const tools = [
    ["제목", () => linePrefix("## ")],
    ["소제목", () => linePrefix("### ")],
    ["굵게", () => wrap("**", "**", "굵은 글씨")],
    ["코드", () => wrap("`", "`", "code")],
    ["코드 블록", () => wrap("```c\n", "\n```", "// 코드")],
    ["목록", () => linePrefix("- ")],
    ["번호", () => linePrefix("1. ")],
    ["인용", () => linePrefix("> ")],
    ["링크", () => wrap("[", "](https://)", "링크 글자")],
    ["표", () => insertBlock("| 항목 | 내용 |\n| --- | --- |\n| | |")],
  ];

  const bar = (
    <>
      <div className="forum-editor__bar">
        {tools.map(([label, fn]) => (
          <button key={label} type="button" className="sstoa-btn sstoa-btn--sm" onClick={fn} disabled={disabled || mode === "preview"}>{label}</button>
        ))}
        <button type="button" className="sstoa-btn sstoa-btn--sm sstoa-btn--primary" onClick={() => fileRef.current?.click()} disabled={disabled || mode === "preview"}>
          사진 넣기
        </button>
        <input
          ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple hidden
          onChange={(e) => { uploadImages(e.target.files); e.target.value = ""; }}
        />
        <span style={{ flex: 1 }} />
        <div className="forum-editor__modes">
          {[["split", "나란히"], ["write", "작성"], ["preview", "미리보기"]].map(([k, l]) => (
            <button key={k} type="button" className={`sstoa-btn sstoa-btn--sm${mode === k ? " sstoa-btn--primary" : ""}`} onClick={() => setMode(k)}>{l}</button>
          ))}
        </div>
      </div>
      {(uploading || error) && (
        <div className={error ? "sstoa-error" : "sstoa-hint"} style={{ marginTop: 6 }}>{error || uploading}</div>
      )}
    </>
  );

  return (
    <div className="sstoa-field forum-editor">
      <label>본문 <span className="req">*</span> <span className="sstoa-hint">— 마크다운. 사진은 '사진 넣기' · 캡처 후 Ctrl+V · 끌어다 놓기로 넣고, 미리보기에서 사진 오른쪽 아래 모서리를 끌면 크기가 바뀝니다. 유튜브 주소만 한 줄에 적으면 영상이 들어갑니다.</span></label>
      {dock ? createPortal(bar, dock) : bar}
      <div
        className={`forum-editor__panes forum-editor__panes--${mode}`}
        onPaste={onPaste}
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        {mode !== "preview" && (
          <textarea
            ref={ref}
            className="sstoa-textarea forum-editor__input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={busy}
            placeholder={"## 개요\n이 글에서 다루는 내용을 적어 주세요.\n\n```c\nHAL_GPIO_TogglePin(GPIOA, GPIO_PIN_5);\n```"}
            spellCheck={false}
          />
        )}
        {mode !== "write" && (
          <div className="forum-editor__preview" tabIndex={0} aria-label="미리보기 — 여기서도 Ctrl+V 로 사진을 넣을 수 있습니다">
            {value.trim()
              ? <Markdown source={value} onImageResize={busy ? undefined : resizeImage} />
              : <p className="sstoa-hint">미리보기가 여기에 나옵니다.</p>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- 첨부 자료 */
/* 새 파일은 고르기만 해 두고(pending), 글을 저장할 때 그 글 전용 폴더로 올라가 첨부된다.
   예전에 올린 파일(자료실)을 다시 거는 것도 된다. */
function AttachPicker({ ids, setIds, pending, setPending, membersOnly, setMembersOnly, busy, addRef }) {
  const [rows, setRows] = useState(null);
  const [limit, setLimit] = useState({ max_bytes: 0, allowed_exts: [] });
  const [pick, setPick] = useState("");
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  async function load() {
    try {
      const [list, meta] = await Promise.all([api.get("/resources"), api.get("/resources/categories")]);
      setRows(list);
      setLimit({ max_bytes: meta.max_bytes || 0, allowed_exts: meta.allowed_exts || [] });
    } catch (err) {
      setError(err.message);
      setRows([]);
    }
  }
  useEffect(() => { load(); }, []);

  const byId = Object.fromEntries((rows || []).filter((r) => r.id != null).map((r) => [r.id, r]));
  /* 저장하면서 막 올라간 파일은 목록에 아직 없으니 한 번 더 불러온다 */
  const unknown = rows !== null && ids.some((id) => !byId[id]);
  useEffect(() => { if (unknown) load(); }, [unknown]);

  const choices = (rows || []).filter((r) => r.id == null || !ids.includes(r.id));

  function addFiles(list) {
    setError("");
    const ok = [];
    for (const f of Array.from(list || [])) {
      const ext = (f.name.match(/\.[^.]+$/) || [""])[0].toLowerCase();
      if (limit.allowed_exts.length && !limit.allowed_exts.includes(ext)) {
        setError(`올릴 수 없는 파일 형식입니다: ${f.name} — 압축(zip)해서 올려 주세요.`);
        continue;
      }
      if (limit.max_bytes && f.size > limit.max_bytes) {
        setError(`파일이 너무 큽니다: ${f.name} (최대 ${fmtSize(limit.max_bytes)})`);
        continue;
      }
      ok.push(f);
    }
    if (ok.length) setPending((prev) => [...prev, ...ok]);
  }
  if (addRef) addRef.current = addFiles; // 본문에 끌어다 놓은 파일도 같은 검사를 거친다

  async function addExisting() {
    if (!pick) return;
    setError("");
    try {
      let row = (rows || []).find((r) => (r.id != null ? String(r.id) : `p:${r.rel_path}`) === pick);
      if (!row) return;
      if (row.id == null) { // 서버에 직접 넣은 파일 — 먼저 목록에 등록
        const fd = new FormData();
        fd.append("rel_path", row.rel_path);
        row = await uploadWithProgress("/resources/register", fd, null);
        await load();
      }
      setIds([...ids, row.id]);
      setPick("");
    } catch (err) {
      setError(err.message);
    }
  }

  /* 회원 전용 ↔ 누구나 — 파일 설정이라 누르는 즉시 적용된다 */
  async function toggleMembers(r) {
    setError("");
    try {
      await api.patch(`/resources/${r.id}`, { members_only: !r.members_only });
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  function move(i, d) {
    const next = [...ids];
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setIds(next);
  }

  return (
    <div className="sstoa-field forum-attach" style={{ marginTop: 14 }}>
      <label>
        첨부 파일 <span className="sstoa-hint">— 글 아래 '첨부 자료'에서 내려받습니다. 본문에 파일을 끌어다 놓아도 여기에 들어옵니다.</span>
      </label>

      {(ids.length > 0 || pending.length > 0) ? (
        <table className="sstoa-table" style={{ marginBottom: 10 }}>
          <tbody>
            {ids.map((id, i) => {
              const r = byId[id];
              return (
                <tr key={id}>
                  <td style={{ fontWeight: 600 }}>
                    {r ? r.title : rows === null || unknown ? "불러오는 중…" : "(삭제된 파일)"}
                    {r && <div className="sstoa-hint">{r.filename} · {fmtSize(r.size)}{r.download_count ? ` · ${r.download_count}회 다운로드` : ""}</div>}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {r && (
                      <button type="button" className={`sstoa-btn sstoa-btn--sm${r.members_only ? " sstoa-btn--primary" : ""}`} onClick={() => toggleMembers(r)} disabled={busy} title="눌러서 회원 전용 / 누구나 전환 (즉시 적용)">
                        {r.members_only ? "회원 전용" : "누구나"}
                      </button>
                    )}{" "}
                    {r && !r.is_public && <span className="sstoa-tag sstoa-tag--gray">비공개 — 안 보임</span>}
                  </td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={() => move(i, -1)} disabled={busy || i === 0} aria-label="위로">▲</button>{" "}
                    <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={() => move(i, 1)} disabled={busy || i === ids.length - 1} aria-label="아래로">▼</button>{" "}
                    <button type="button" className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => setIds(ids.filter((x) => x !== id))} disabled={busy}>빼기</button>
                  </td>
                </tr>
              );
            })}
            {pending.map((f, i) => (
              <tr key={`${f.name}-${f.size}-${f.lastModified}-${i}`} className="forum-attach__pending">
                <td style={{ fontWeight: 600 }}>
                  {f.name}
                  <div className="sstoa-hint">{fmtSize(f.size)} · 저장할 때 올라갑니다</div>
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <span className={`sstoa-tag${membersOnly ? "" : " sstoa-tag--gray"}`}>{membersOnly ? "회원 전용" : "누구나"}</span>
                </td>
                <td style={{ textAlign: "right" }}>
                  <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={() => setPending((prev) => prev.filter((_, j) => j !== i))} disabled={busy}>선택 취소</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="sstoa-hint" style={{ marginBottom: 10 }}>첨부 파일이 없습니다.</div>
      )}

      <div className="sstoa-row" style={{ alignItems: "center", gap: 12 }}>
        <button type="button" className="sstoa-btn sstoa-btn--primary" onClick={() => fileRef.current?.click()} disabled={busy}>+ 파일 추가</button>
        <input ref={fileRef} type="file" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontWeight: 400 }}>
          <input type="checkbox" checked={membersOnly} onChange={(e) => setMembersOnly(e.target.checked)} disabled={busy} />
          새 파일은 회원만 내려받기
        </label>
        <span className="sstoa-hint">최대 {limit.max_bytes ? fmtSize(limit.max_bytes) : "…"}</span>
      </div>

      <details className="forum-attach__reuse">
        <summary>예전에 올린 파일에서 고르기</summary>
        <div className="sstoa-row" style={{ alignItems: "flex-end", marginTop: 8 }}>
          <div className="sstoa-field" style={{ flex: 2 }}>
            <select className="sstoa-select" value={pick} onChange={(e) => setPick(e.target.value)} disabled={busy}>
              <option value="">선택하세요 ({choices.length}개)</option>
              {choices.map((r) => {
                const v = r.id != null ? String(r.id) : `p:${r.rel_path}`;
                return <option key={v} value={v}>{r.title} — {r.filename}{r.id == null ? " (미등록)" : ""}</option>;
              })}
            </select>
          </div>
          <button type="button" className="sstoa-btn" onClick={addExisting} disabled={busy || !pick}>첨부</button>
        </div>
      </details>
      {error && <div className="sstoa-error" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}

/* ---------------------------------------------------------------- 글 편집 */
function PostForm({ initial, onSaved, onClose }) {
  const [form, setForm] = useState(() => (initial ? toForm(initial) : EMPTY));
  const [baseline, setBaseline] = useState(() => JSON.stringify(initial ? toForm(initial) : EMPTY));
  const [saved, setSaved] = useState(initial || null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dock, setDock] = useState(null); // 팝업 맨 위 도구 막대 자리
  const [pending, setPending] = useState([]); // 저장할 때 올릴 새 첨부 파일
  const [membersOnly, setMembersOnly] = useState(true);
  const [progress, setProgress] = useState("");
  const addFilesRef = useRef(null);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!form.category) { setError("분류를 선택해 주세요."); return; }
    if (!form.title.trim()) { setError("제목을 입력해 주세요."); return; }
    if (!form.body.trim()) { setError("본문을 입력해 주세요."); return; }
    setBusy(true); setError("");
    try {
      const payload = { ...form, title: form.title.trim() };
      let res = saved ? await api.post(`/forum/posts/${saved.id}`, payload) : await api.post("/forum/posts", payload);
      setSaved(res);
      setBaseline(JSON.stringify(toForm(res)));
      onSaved(res);

      /* 새 파일은 글이 저장된 뒤(글 ID 가 생긴 뒤) 하나씩 올린다.
         중간에 실패해도 올라간 파일은 첨부로 남고, 남은 것만 다시 저장하면 된다. */
      const queue = [...pending];
      const total = queue.length;
      while (queue.length > 0) {
        const n = total - queue.length + 1;
        const fd = new FormData();
        fd.append("file", queue[0]);
        fd.append("members_only", membersOnly ? "true" : "false");
        const label = `파일 올리는 중 (${n}/${total})`;
        const next = await uploadWithProgress(`/forum/posts/${res.id}/files`, fd, (pct) => setProgress(`${label} ${pct}%`));
        res = next;
        setSaved(next);
        setForm((f) => ({ ...f, attachments: next.attachments }));
        setBaseline(JSON.stringify(toForm(next)));
        onSaved(next);
        queue.shift();
        setPending([...queue]);
      }
      setProgress("");
      onClose(); // 글 저장과 파일 업로드가 모두 끝났을 때만 닫는다
    } catch (err) {
      setError(err.message);
      setProgress("");
    } finally {
      setBusy(false);
    }
  }

  function cancel() {
    if (busy) return;
    if ((JSON.stringify(form) !== baseline || pending.length > 0) && !window.confirm("저장하지 않은 변경 내용이 있습니다. 저장하지 않고 닫을까요?")) return;
    onClose();
  }

  return (
    <Modal title={saved ? `포럼 글 수정 — ${saved.title}` : "포럼 글 쓰기"} onClose={cancel} busy={busy} dockRef={setDock}>
      <form className="sstoa-modal__form" onSubmit={submit}>
        <div className="sstoa-modal__body">
          <div className="sstoa-row">
            <div className="sstoa-field">
              <label>분류 <span className="req">*</span></label>
              <select className="sstoa-select" value={form.category} onChange={upd("category")} disabled={busy}>
                <option value="" disabled>선택하세요</option>
                {FORUM_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div className="sstoa-field" style={{ flex: 4 }}>
              <label>제목 <span className="req">*</span></label>
              <input className="sstoa-input" value={form.title} onChange={upd("title")} disabled={busy} maxLength={200} />
            </div>
          </div>

          <div className="sstoa-field" style={{ marginTop: 12 }}>
            <label>요약 <span className="sstoa-hint">— 목록에 보이는 한두 문장 · 비우면 본문 앞부분을 씁니다</span></label>
            <textarea className="sstoa-textarea" rows={2} value={form.summary} onChange={upd("summary")} disabled={busy} />
          </div>

          <div style={{ marginTop: 12 }}>
            <BodyEditor
              value={form.body} onChange={(body) => setForm((f) => ({ ...f, body }))} busy={busy} dock={dock}
              onAttach={(files) => addFilesRef.current?.(files)}
            />
          </div>

          <AttachPicker
            ids={form.attachments} setIds={(attachments) => setForm((f) => ({ ...f, attachments }))}
            pending={pending} setPending={setPending}
            membersOnly={membersOnly} setMembersOnly={setMembersOnly}
            busy={busy} addRef={addFilesRef}
          />

          <div className="sstoa-row" style={{ marginTop: 14 }}>
            <label style={{ display: "inline-flex", gap: 8, alignItems: "center", fontWeight: 600 }}>
              <input type="checkbox" checked={form.is_public} disabled={busy} onChange={(e) => setForm((f) => ({ ...f, is_public: e.target.checked }))} />
              {form.is_public ? "공개 — 홈페이지 포럼에 보임" : "비공개(임시 저장) — 홈페이지에 안 보임"}
            </label>
            <label style={{ display: "inline-flex", gap: 8, alignItems: "center", fontWeight: 600 }}>
              <input type="checkbox" checked={form.is_pinned} disabled={busy} onChange={(e) => setForm((f) => ({ ...f, is_pinned: e.target.checked }))} />
              공지 — 목록 맨 위에 고정
            </label>
          </div>
        </div>

        <div className="sstoa-modal__foot">
          {error && <div className="sstoa-error sstoa-modal__error">{error}</div>}
          {progress && <span className="sstoa-hint">{progress}</span>}
          {saved && saved.is_public && (
            <a className="sstoa-btn sstoa-btn--sm" href={siteUrl(saved)} target="_blank" rel="noopener noreferrer">사이트에서 보기</a>
          )}
          <span style={{ flex: 1 }} />
          <button type="button" className="sstoa-btn" onClick={cancel} disabled={busy}>취소</button>
          <button type="submit" className="sstoa-btn sstoa-btn--primary" disabled={busy}>
            {busy ? "저장 중…" : saved ? "저장" : "등록"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ---------------------------------------------------------------- 글 목록 탭 */
function PostsTab({ onShowComments }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | post
  const [cat, setCat] = useState("");
  const [error, setError] = useState("");

  async function load() {
    setError("");
    try { setRows(await api.get("/forum/posts")); }
    catch (err) { setError(err.message); setRows([]); }
  }
  useEffect(() => { load(); }, []);

  async function open(p) {
    setError("");
    try { setEditing(await api.get(`/forum/posts/${p.id}`)); }
    catch (err) { setError(err.message); }
  }

  async function togglePublic(p) {
    try { await api.post(`/forum/posts/${p.id}`, { is_public: !p.is_public }); load(); }
    catch (err) { setError(err.message); }
  }

  async function remove(p) {
    if (!window.confirm(`'${p.title}' 글을 삭제할까요?\n달린 댓글도 함께 지워집니다. 첨부 파일은 자료실 저장소에 남습니다.`)) return;
    try { await api.del(`/forum/posts/${p.id}`); load(); }
    catch (err) { setError(err.message); }
  }

  const shown = (rows || []).filter((p) => !cat || p.category === cat);

  return (
    <>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "14px 0" }}>
        <select className="sstoa-select" style={{ width: "auto" }} value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="">전체 분류</option>
          {FORUM_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <span className="sstoa-hint">{shown.length}건</span>
        <span style={{ flex: 1 }} />
        <button className="sstoa-btn sstoa-btn--primary" onClick={() => setEditing("new")}>+ 글 쓰기</button>
      </div>

      {error && <div className="sstoa-error" style={{ marginBottom: 12 }}>{error}</div>}

      {editing && (
        <PostForm
          key={editing === "new" ? "new" : editing.id}
          initial={editing === "new" ? null : editing}
          onSaved={() => load()}
          onClose={() => { setEditing(null); load(); }}
        />
      )}

      <div className="sstoa-panel">
        {rows === null ? (
          <div className="sstoa-empty">불러오는 중…</div>
        ) : shown.length === 0 ? (
          <div className="sstoa-empty">글이 없습니다.</div>
        ) : (
          <table className="sstoa-table">
            <thead>
              <tr><th>공개</th><th>분류</th><th></th><th>제목</th><th>첨부</th><th>댓글</th><th>조회</th><th>작성</th><th></th></tr>
            </thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.id}>
                  <td>
                    <button
                      type="button"
                      className={`sstoa-btn sstoa-btn--sm${p.is_public ? " sstoa-btn--primary" : ""}`}
                      onClick={() => togglePublic(p)}
                      title="눌러서 공개 / 비공개 전환"
                    >
                      {p.is_public ? "공개" : "비공개"}
                    </button>
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>{CAT_LABEL[p.category] || p.category}</td>
                  <td style={{ width: 64 }}>
                    {p.cover && <img src={p.cover} alt="" style={{ width: 56, height: 42, objectFit: "cover", borderRadius: 6, display: "block" }} />}
                  </td>
                  <td style={{ fontWeight: 600 }}>
                    {p.is_pinned && <span className="sstoa-tag" style={{ marginRight: 6 }}>공지</span>}
                    {p.title}
                  </td>
                  <td>{p.attachments.length || "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {p.comments}
                    {p.pending > 0 && (
                      <button type="button" className="sstoa-tag" style={{ marginLeft: 6, border: 0, cursor: "pointer", background: "var(--s-danger-weak)", color: "var(--s-danger)" }} onClick={onShowComments}>
                        답변 대기 {p.pending}
                      </button>
                    )}
                  </td>
                  <td>{p.views}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{fmtWhen(p.created_at).slice(0, 10)}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {p.is_public && (
                      <><a className="sstoa-btn sstoa-btn--sm" href={siteUrl(p)} target="_blank" rel="noopener noreferrer">보기</a>{" "}</>
                    )}
                    <button className="sstoa-btn sstoa-btn--sm" onClick={() => open(p)}>수정</button>{" "}
                    <button className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => remove(p)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

/* ---------------------------------------------------------------- 댓글 탭 */
function CommentLine({ c, onDelete }) {
  return (
    <div className={`forum-thread__line${c.is_admin ? " is-admin" : ""}${c.deleted ? " is-deleted" : ""}`}>
      <div className="forum-thread__who">
        <strong>{c.author}</strong>
        {c.email && <span className="sstoa-hint">{c.email}</span>}
        {c.is_secret && <span className="sstoa-tag sstoa-tag--gray">비밀</span>}
        {c.deleted && <span className="sstoa-tag sstoa-tag--gray">삭제됨</span>}
        <span className="sstoa-hint">{fmtWhen(c.created_at)}</span>
        <span style={{ flex: 1 }} />
        {!c.deleted && (
          <button type="button" className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => onDelete(c)}>삭제</button>
        )}
      </div>
      <div className="forum-thread__body">{c.body}</div>
    </div>
  );
}

function Thread({ t, onChanged }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function reply(e) {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true); setError("");
    try {
      await api.post(`/forum/comments/${t.id}/reply`, { body: text.trim() });
      setText("");
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(c) {
    if (!window.confirm("이 댓글을 삭제할까요? 홈페이지에서 보이지 않게 됩니다.")) return;
    try { await api.del(`/forum/comments/${c.id}`); onChanged(); }
    catch (err) { setError(err.message); }
  }

  return (
    <div className="sstoa-panel forum-thread">
      <div className="forum-thread__post">
        {t.pending && <span className="sstoa-tag" style={{ background: "var(--s-danger-weak)", color: "var(--s-danger)" }}>답변 대기</span>}
        <a className="sstoa-link" href={`/forum/${t.post_id}#comments`} target="_blank" rel="noopener noreferrer">{t.post_title || `글 #${t.post_id}`}</a>
      </div>
      <CommentLine c={t} onDelete={remove} />
      {t.replies.map((r) => <div key={r.id} className="forum-thread__reply"><CommentLine c={r} onDelete={remove} /></div>)}
      {!t.deleted && (
        <form className="forum-thread__form" onSubmit={reply}>
          <textarea
            className="sstoa-textarea" rows={2} value={text} onChange={(e) => setText(e.target.value)} disabled={busy}
            placeholder={`답변 입력 — I.META 이름으로 달리고${t.email ? ", 작성자에게 메일로 알립니다" : ""}`}
          />
          <button type="submit" className="sstoa-btn sstoa-btn--primary" disabled={busy || !text.trim()}>
            {busy ? "등록 중…" : "답변 등록"}
          </button>
        </form>
      )}
      {error && <div className="sstoa-error" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}

function CommentsTab({ onChanged }) {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  async function load(s = status) {
    setError("");
    try { setRows(await api.get(`/forum/comments?status=${s}`)); }
    catch (err) { setError(err.message); setRows([]); }
  }
  useEffect(() => { setRows(null); load(status); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  function changed() { load(); onChanged(); }

  return (
    <>
      <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "14px 0" }}>
        {[["pending", "답변 대기"], ["all", "전체"]].map(([k, l]) => (
          <button key={k} type="button" className={`sstoa-btn sstoa-btn--sm${status === k ? " sstoa-btn--primary" : ""}`} onClick={() => setStatus(k)}>{l}</button>
        ))}
        <span className="sstoa-hint">회원이 남긴 댓글에 운영자 답이 없거나, 답 뒤에 다시 물은 것이 '답변 대기'입니다.</span>
      </div>
      {error && <div className="sstoa-error" style={{ marginBottom: 12 }}>{error}</div>}
      {rows === null ? (
        <div className="sstoa-panel"><div className="sstoa-empty">불러오는 중…</div></div>
      ) : rows.length === 0 ? (
        <div className="sstoa-panel"><div className="sstoa-empty">{status === "pending" ? "답변을 기다리는 댓글이 없습니다." : "댓글이 없습니다."}</div></div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {rows.map((t) => <Thread key={t.id} t={t} onChanged={changed} />)}
        </div>
      )}
    </>
  );
}

export default function ForumAdmin() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "comments" ? "comments" : "posts";
  const [pending, setPending] = useState(0);

  async function loadCount() {
    try { setPending((await api.get("/forum/counts")).PENDING || 0); }
    catch { setPending(0); }
  }
  useEffect(() => { loadCount(); }, [tab]);

  return (
    <div>
      <StickyHead>
        <div style={{ marginBottom: 12 }}>
          <Link to="/sstoa" className="sstoa-back">← 홈</Link>
        </div>

        <div className="sstoa-topbar">
          <div>
            <h1 className="sstoa-title">포럼 관리</h1>
            <p className="sstoa-sub">
              홈페이지 <a className="sstoa-link" href="/forum" target="_blank" rel="noreferrer">포럼</a>의 글은 여기서만 쓸 수 있습니다.
              공개로 저장하면 바로 반영되고, 회원이 남긴 댓글(문의)에는 '댓글 문의' 탭에서 답합니다.
            </p>
          </div>
        </div>

        <div className="sstoa-tabs">
          <button type="button" className={`sstoa-tab${tab === "posts" ? " is-active" : ""}`} onClick={() => setParams({})}>글</button>
          <button type="button" className={`sstoa-tab${tab === "comments" ? " is-active" : ""}`} onClick={() => setParams({ tab: "comments" })}>
            댓글 문의{pending > 0 && <span className="sstoa-tab__cnt">답변 대기 {pending}</span>}
          </button>
        </div>
      </StickyHead>

      {tab === "posts"
        ? <PostsTab onShowComments={() => setParams({ tab: "comments" })} />
        : <CommentsTab onChanged={loadCount} />}
    </div>
  );
}
